<template>
    <div class="p-3">
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>

        <template v-else-if="detalle">
            <!-- Encabezado del inmueble -->
            <div class="det-header">
                <div>
                    <p class="det-titulo">{{ inmueble.titulo || `Inmueble #${inmueble.id}` }}</p>
                    <p class="det-sub">
                        <span v-if="inmueble.codigo_ficha" class="badge bg-light text-dark border me-1">{{ inmueble.codigo_ficha }}</span>
                        #{{ inmueble.id }}
                        <template v-if="inmueble.direccion"> · {{ inmueble.direccion }}</template>
                        <template v-if="inmueble.ciudad"> · {{ inmueble.ciudad.nombre }}</template>
                    </p>
                    <p class="det-sub">
                        <template v-if="inmueble.tipo_inmueble">{{ inmueble.tipo_inmueble.nombre }}</template>
                        <template v-if="inmueble.tipo_negocio"> · {{ inmueble.tipo_negocio.nombre }}</template>
                        <template v-if="inmueble.estado"> · {{ inmueble.estado.nombre }}</template>
                    </p>
                    <p class="det-sub">
                        <i class="fas fa-user-tie me-1"></i>
                        {{ inmueble.agente ? `${inmueble.agente.nombre} — ${inmueble.agente.email}` : 'Sin agente' }}
                    </p>
                </div>
                <div class="text-end">
                    <p class="det-promedio">{{ formatPromedio(reputacion.promedio_general) }}</p>
                    <estrellas :valor="reputacion.promedio_general" />
                    <div class="mt-1">
                        <span class="nivel-chip" :style="nivelStyle(reputacion.nivel)">{{ nivelNombre(reputacion.nivel) }}</span>
                    </div>
                    <p class="det-sub mt-1">
                        Histórico: {{ formatPromedio(inmueble.promedio_historico) }}
                        ({{ formatNumber(inmueble.total_calificaciones_historico) }} calif.)
                    </p>
                </div>
            </div>

            <!-- KPIs del periodo -->
            <div class="det-kpis">
                <div class="det-kpi">
                    <span class="det-kpi__label">Calificaciones</span>
                    <span class="det-kpi__value">{{ formatNumber(reputacion.total_calificaciones) }}</span>
                </div>
                <div class="det-kpi">
                    <span class="det-kpi__label">Prom. inmueble</span>
                    <span class="det-kpi__value">{{ formatPromedio(reputacion.promedio_inmueble) }}</span>
                </div>
                <div class="det-kpi">
                    <span class="det-kpi__label">Prom. agente</span>
                    <span class="det-kpi__value">{{ formatPromedio(reputacion.promedio_agente) }}</span>
                </div>
                <div class="det-kpi">
                    <span class="det-kpi__label">Positivas / Neutras / Negativas</span>
                    <span class="det-kpi__value">
                        <span class="text-success">{{ reputacion.positivas || 0 }}</span> /
                        <span class="text-warning">{{ reputacion.neutras || 0 }}</span> /
                        <span class="text-danger">{{ reputacion.negativas || 0 }}</span>
                    </span>
                </div>
                <div class="det-kpi">
                    <span class="det-kpi__label">Última calificación</span>
                    <span class="det-kpi__value det-kpi__value--sm">{{ formatFechaHora(reputacion.ultima_calificacion) }}</span>
                </div>
            </div>

            <div v-if="!reputacion.total_calificaciones" class="text-center text-secondary text-sm py-4">
                <i class="fas fa-star-half-alt d-block mb-2" style="font-size:1.8rem;opacity:.35"></i>
                Este inmueble no recibió calificaciones en el periodo seleccionado.
            </div>

            <div v-else class="row g-3">
                <!-- Criterios -->
                <div class="col-md-6">
                    <p class="det-seccion">Criterios</p>
                    <div v-for="c in detalle.criterios" :key="c.clave" class="crit-row">
                        <div class="d-flex justify-content-between">
                            <span class="crit-row__name">
                                {{ c.nombre }}
                                <span class="crit-row__grupo">{{ c.grupo }}</span>
                            </span>
                            <span class="crit-row__val">{{ formatPromedio(c.promedio) }}</span>
                        </div>
                        <div class="bar">
                            <div class="bar__fill" :style="{ width: (c.porcentaje || 0) + '%', background: colorPorPromedio(c.promedio) }"></div>
                        </div>
                    </div>
                </div>

                <!-- Distribución -->
                <div class="col-md-6">
                    <p class="det-seccion">Distribución de estrellas</p>
                    <div v-for="d in detalle.distribucion" :key="d.estrellas" class="dist-row">
                        <span class="dist-row__label">{{ d.estrellas }} <i class="fas fa-star"></i></span>
                        <div class="bar flex-grow-1">
                            <div class="bar__fill" :style="{ width: (d.porcentaje || 0) + '%', background: colorPorPromedio(d.estrellas) }"></div>
                        </div>
                        <span class="dist-row__val">{{ d.total }}</span>
                    </div>
                </div>

                <!-- Calificaciones -->
                <div class="col-12">
                    <p class="det-seccion">Calificaciones del periodo</p>
                    <div class="table-responsive">
                        <table class="table align-middle mb-0 det-tabla">
                            <thead>
                                <tr>
                                    <th>Calificador</th>
                                    <th class="text-center">General</th>
                                    <th class="text-center" v-for="c in detalle.criterios" :key="c.clave" :title="c.nombre">
                                        {{ abreviatura(c.clave) }}
                                    </th>
                                    <th>Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="cal in detalle.calificaciones" :key="cal.id">
                                    <td>
                                        <p class="mb-0 text-sm fw-semibold">{{ cal.calificador?.nombre || 'Usuario' }}</p>
                                        <p class="mb-0 text-xxs text-secondary">{{ cal.calificador?.email }}</p>
                                        <p v-if="cal.inmobiliaria" class="mb-0 text-xxs text-secondary">
                                            <i class="fas fa-building me-1"></i>{{ cal.inmobiliaria.nombre }}
                                        </p>
                                    </td>
                                    <td class="text-center">
                                        <span class="nivel-chip" :style="nivelStyle(cal.nivel)">{{ formatPromedio(cal.promedio_general) }}</span>
                                    </td>
                                    <td class="text-center text-sm" v-for="c in detalle.criterios" :key="c.clave">
                                        {{ valorCriterio(cal, c.clave) }}
                                    </td>
                                    <td class="text-xs text-secondary">
                                        {{ formatFechaHora(cal.actualizada) }}
                                        <span v-if="cal.creada && cal.actualizada && cal.creada !== cal.actualizada"
                                            class="d-block text-xxs" :title="`Creada: ${formatFechaHora(cal.creada)}`">
                                            (editada)
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { computed } from "vue";
import Estrellas from "./estrellas.vue";
import {
    nivelStyle, nivelNombre, colorPorPromedio,
    formatPromedio, formatNumber, formatFechaHora,
} from "../reputacionHelpers.js";

const ABREVIATURAS = {
    fotos: 'Fotos',
    informacion: 'Info.',
    disponibilidad: 'Disp.',
    respuesta: 'Resp.',
    etica: 'Ética',
};

export default {
    name: "DetalleInmuebleReputacion",
    components: { Estrellas },
    props: {
        detalle: { type: Object, default: null },
        loading: { type: Boolean, default: false },
    },
    setup(props) {
        const inmueble = computed(() => props.detalle?.inmueble || {});
        const reputacion = computed(() => props.detalle?.reputacion || {});
        const abreviatura = (clave) => ABREVIATURAS[clave] || clave;
        const valorCriterio = (cal, clave) => {
            const v = cal.criterios ? cal.criterios[clave] : null;
            return v === null || v === undefined ? '—' : v;
        };

        return {
            inmueble, reputacion, abreviatura, valorCriterio,
            nivelStyle, nivelNombre, colorPorPromedio,
            formatPromedio, formatNumber, formatFechaHora,
        };
    },
};
</script>

<style lang="scss" scoped>
.det-header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding-bottom: 16px;
    border-bottom: 1px solid #F1F5F9;
    margin-bottom: 16px;
}

.det-titulo {
    font-size: 1rem;
    font-weight: 700;
    color: #1E293B;
    margin: 0 0 4px;
}

.det-sub {
    font-size: 0.75rem;
    color: #64748B;
    margin: 0 0 2px;
}

.det-promedio {
    font-size: 2rem;
    font-weight: 700;
    color: #1E293B;
    line-height: 1;
    margin: 0 0 4px;
}

.det-kpis {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
}

.det-kpi {
    flex: 1 1 140px;
    border: 1px solid #E2E8F0;
    border-radius: 0.5rem;
    padding: 8px 12px;

    &__label {
        display: block;
        font-size: 0.66rem;
        color: #94A3B8;
    }

    &__value {
        display: block;
        font-size: 0.95rem;
        font-weight: 700;
        color: #1E293B;

        &--sm {
            font-size: 0.78rem;
        }
    }
}

.det-seccion {
    font-size: 0.8rem;
    font-weight: 600;
    color: #1E293B;
    margin-bottom: 8px;
}

.det-tabla {
    th {
        font-size: 0.65rem;
        text-transform: uppercase;
        color: #64748B;
        font-weight: 700;
    }
}

.nivel-chip {
    display: inline-block;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 0.7rem;
    font-weight: 600;
}

.crit-row {
    padding: 6px 0;

    &__name {
        font-size: 0.76rem;
        color: #334155;
    }

    &__grupo {
        font-size: 0.62rem;
        color: #94A3B8;
        text-transform: uppercase;
        margin-left: 4px;
    }

    &__val {
        font-size: 0.78rem;
        font-weight: 700;
        color: #1E293B;
    }
}

.dist-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 0;

    &__label {
        font-size: 0.74rem;
        color: #334155;
        width: 34px;

        i {
            color: #F59E0B;
            font-size: 0.65rem;
        }
    }

    &__val {
        font-size: 0.74rem;
        font-weight: 600;
        color: #334155;
        width: 32px;
        text-align: right;
    }
}

.bar {
    height: 6px;
    border-radius: 999px;
    background: #F1F5F9;
    overflow: hidden;
    margin-top: 4px;

    &__fill {
        height: 100%;
        border-radius: 999px;
        transition: width 0.3s ease;
    }
}

.dist-row .bar {
    margin-top: 0;
}
</style>
